import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const Farms = () => {
  const { language, isRTL } = useLanguage();

  const farmingPractices = language === 'ar' ? [
    {
      id: 1,
      title: 'تغذية طبيعية',
      description: 'تُغذى ماشيتنا بأعلاف طبيعية وعضوية بدون هرمونات أو مضادات حيوية.'
    },
    {
      id: 2,
      title: 'معاملة أخلاقية',
      description: 'نضمن معاملة إنسانية وظروف معيشية مناسبة لجميع الحيوانات.'
    },
    {
      id: 3,
      title: 'مراقبة صحية',
      description: 'فحوصات بيطرية منتظمة ومراقبة صحية لضمان سلامة الماشية.'
    },
    {
      id: 4,
      title: 'معالجة صحية',
      description: 'مرافق حديثة تحافظ على أعلى معايير النظافة.'
    }
  ] : [
    {
      id: 1,
      title: 'Natural Feed',
      description: 'Our livestock is fed with natural, organic feed without hormones or antibiotics.'
    },
    {
      id: 2,
      title: 'Ethical Treatment',
      description: 'We ensure humane treatment and proper living conditions for all animals.'
    },
    {
      id: 3,
      title: 'Health Monitoring',
      description: 'Regular veterinary checks and health monitoring for optimal livestock wellness.'
    },
    {
      id: 4,
      title: 'Hygienic Processing',
      description: 'State-of-the-art facilities maintaining the highest hygiene standards.'
    }
  ];

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            {language === 'ar' ? 'مزارعنا والإنتاج' : 'Our Farms & Production'}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {language === 'ar' 
              ? 'حيث تبدأ الجودة - ممارسات زراعية مستدامة ومرافق حديثة'
              : 'Where quality begins - sustainable farming practices and modern facilities'}
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'md:order-2' : ''}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'تميز زراعي حديث' : 'Modern Farming Excellence'}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'تمتد مزارعنا عبر أراضٍ زراعية ممتازة، توفر الظروف المثلى لتربية ماشية صحية. نجمع بين الحكمة الزراعية التقليدية والتقنيات الحديثة لضمان أفضل جودة لإنتاج اللحوم.'
                  : 'Our farms span across prime agricultural land, providing optimal conditions for raising healthy livestock. We combine traditional farming wisdom with modern techniques to ensure the best quality meat production.'}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'كل جانب من عملياتنا الزراعية مصمم لإعطاء الأولوية لرعاية الحيوان والاستدامة البيئية وجودة المنتج. من إدارة المراعي إلى الرعاية البيطرية، نحافظ على أعلى المعايير.'
                  : 'Every aspect of our farming operation is designed to prioritize animal welfare, environmental sustainability, and product quality. From pasture management to veterinary care, we maintain the highest standards.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {language === 'ar'
                  ? 'مرافق المعالجة الحديثة لدينا مجهزة بتقنيات متقدمة لضمان النظافة والتتبع وسلامة الغذاء في كل خطوة من خطوات الإنتاج.'
                  : 'Our state-of-the-art processing facilities are equipped with advanced technology to ensure hygiene, traceability, and food safety at every step of production.'}
              </p>
            </div>
            <div className={isRTL ? 'md:order-1' : ''}>
              <img
                src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800"
                alt={language === 'ar' ? 'عمليات المزرعة' : 'Farm Operations'}
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Farming Practices */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'ممارساتنا الزراعية' : 'Our Farming Practices'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'ar'
                ? 'ملتزمون بالتميز في كل جانب من رعاية الماشية والإنتاج'
                : 'Committed to excellence in every aspect of livestock care and production'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {farmingPractices.map((practice) => (
              <Card key={practice.id} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">{practice.title}</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">{practice.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'مرافقنا' : 'Our Facilities'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'ar'
                ? 'بنية تحتية حديثة لمعالجة وتوزيع منتجات اللحوم'
                : 'Modern infrastructure for meat processing and distribution'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <img
                  src="https://images.unsplash.com/photo-1560493676-04071c5f467b?w=400"
                  alt={language === 'ar' ? 'منشأة المعالجة' : 'Processing Facility'}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {language === 'ar' ? 'منشأة المعالجة' : 'Processing Facility'}
                </h3>
                <p className="text-gray-700">
                  {language === 'ar'
                    ? 'مجهزة بأحدث الآلات لمعالجة اللحوم'
                    : 'Equipped with latest machinery for meat processing'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <img
                  src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400"
                  alt={language === 'ar' ? 'التخزين البارد' : 'Cold Storage'}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {language === 'ar' ? 'التخزين البارد' : 'Cold Storage'}
                </h3>
                <p className="text-gray-700">
                  {language === 'ar'
                    ? 'تخزين بارد بسعة كبيرة لحفظ المنتجات الطازجة'
                    : 'High-capacity cold storage for preserving fresh products'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400"
                  alt={language === 'ar' ? 'أسطول التوزيع' : 'Distribution Fleet'}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {language === 'ar' ? 'أسطول التوزيع' : 'Distribution Fleet'}
                </h3>
                <p className="text-gray-700">
                  {language === 'ar'
                    ? 'شاحنات مبردة للتوصيل في الوقت المناسب'
                    : 'Refrigerated trucks for timely delivery'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'ar' ? 'هل تريد زيارة مزارعنا؟' : 'Want to Visit Our Farms?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'ar'
              ? 'نرحب بالعملاء لزيارة مرافقنا ورؤية التزامنا بالجودة مباشرة.'
              : 'We welcome customers to visit our facilities and see our commitment to quality firsthand.'}
          </p>
          <a 
            href="/contact" 
            className="inline-block bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {language === 'ar' ? 'احجز زيارة' : 'Schedule a Visit'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Farms;
